// The allowed range of each blank-able numeric setting the Config Manager
// checks before saving (backend PRD 39 page-hint retry, PRD 40 chunking),
// copied from legalflow-idp configs/schemas. The backend stays the authority:
// it rejects the same values on save and at publish. Checking here as well
// shows the error at the field and stops a save the backend would refuse -
// or, against an older backend, silently accept.
export interface SettingRange {
  min: number
  max?: number
  integer: boolean
}

export const SETTING_RANGES = {
  // global_defaults.chunking / document_types.<id>.defaults.chunking
  target_pages_per_chunk_default: { min: 1, integer: true },
  max_concurrent_chunks: { min: 1, max: 10, integer: true },
  // global_defaults.page_hint_retry / document_types.<id>.defaults.page_hint_retry
  max_widen_radius_default: { min: 0, integer: true },
  whole_document_threshold_percent_default: { min: 0, max: 100, integer: false },
  max_extra_bedrock_calls_per_request: { min: 0, integer: true },
  retry_time_reserve_seconds: { min: 0, max: 28, integer: false },
} satisfies Record<string, SettingRange>

export type RangedSetting = keyof typeof SETTING_RANGES

// The backend's own wording (gui_backend/core/validation.py), so the message
// at the field matches the one a refused save would show.
export function rangeError(key: RangedSetting, value: number | undefined): string | undefined {
  if (value === undefined) return undefined
  const range: SettingRange = SETTING_RANGES[key]
  if (range.integer && !Number.isInteger(value)) return `'${key}' must be a whole number`
  if (value < range.min) return `'${key}' must be at least ${range.min}`
  if (range.max !== undefined && value > range.max) return `'${key}' must be at most ${range.max}`
  return undefined
}

// True when any ranged setting in `block` (a chunking or page_hint_retry
// object, possibly absent) is out of range.
export function hasRangeErrors(block: Partial<Record<RangedSetting, number>> | undefined): boolean {
  if (!block) return false
  return (Object.keys(SETTING_RANGES) as RangedSetting[]).some(
    (key) => rangeError(key, block[key]) !== undefined,
  )
}
